import React, { useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/16/solid'
import { Field, Label, Switch } from '@headlessui/react'
import { useTheme } from '../../context/ThemeContext'

export const Contact: React.FC = () => {
  const [agreed, setAgreed] = useState(false)
  const { isDarkMode } = useTheme()

  return (
    <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-white'} px-6 py-24 sm:py-16 lg:px-8`}>
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-[-10rem] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[-20rem]"
      >
        <div
          style={{
            clipPath:
              'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
          }}
          className="relative left-1/2 -z-10 aspect-[1155/678] w-[36.125rem] max-w-none -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-40rem)] sm:w-[72.1875rem]"
        />
      </div>
      <div className="mx-auto max-w-2xl text-center">
        <h2 className={`text-balance text-4xl font-semibold tracking-tight sm:text-5xl ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Get in Touch
        </h2>
        <p className={`mt-6 text-lg leading-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Have questions about our platform? We're here to help. Fill out the form below and we'll get back to you as soon as possible.
        </p>
      </div>
      <form action="#" method="POST" className="mx-auto mt-16 max-w-xl sm:mt-20">
        <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
          <div>
            <label htmlFor="first-name" className={`block text-sm/6 font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              First name
            </label>
            <div className="mt-2.5">
              <input
                id="first-name"
                name="first-name"
                type="text"
                autoComplete="given-name"
                className={`block w-full rounded-md px-3.5 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-accent-primary ${
                  isDarkMode 
                    ? 'bg-gray-800 text-white outline-gray-700' 
                    : 'bg-white text-gray-900 outline-gray-300'
                }`}
              />
            </div>
          </div>
          <div>
            <label htmlFor="last-name" className={`block text-sm/6 font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Last name
            </label>
            <div className="mt-2.5">
              <input
                id="last-name"
                name="last-name"
                type="text"
                autoComplete="family-name"
                className={`block w-full rounded-md px-3.5 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-accent-primary ${
                  isDarkMode 
                    ? 'bg-gray-800 text-white outline-gray-700' 
                    : 'bg-white text-gray-900 outline-gray-300'
                }`}
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="company" className={`block text-sm/6 font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Company
            </label>
            <div className="mt-2.5">
              <input
                id="company"
                name="company"
                type="text"
                autoComplete="organization"
                className={`block w-full rounded-md px-3.5 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-accent-primary ${
                  isDarkMode 
                    ? 'bg-gray-800 text-white outline-gray-700' 
                    : 'bg-white text-gray-900 outline-gray-300'
                }`}
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="email" className={`block text-sm/6 font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Email
            </label>
            <div className="mt-2.5">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={`block w-full rounded-md px-3.5 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-accent-primary ${
                  isDarkMode 
                    ? 'bg-gray-800 text-white outline-gray-700' 
                    : 'bg-white text-gray-900 outline-gray-300'
                }`}
              />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="phone-number" className={`block text-sm/6 font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Phone number
            </label>
            <div className="mt-2.5">
              <div className={`flex rounded-md outline outline-1 -outline-offset-1 has-[input:focus-within]:outline has-[input:focus-within]:outline-2 has-[input:focus-within]:-outline-offset-2 has-[input:focus-within]:outline-accent-primary ${
                isDarkMode 
                  ? 'bg-gray-800 outline-gray-700' 
                  : 'bg-white outline-gray-300'
              }`}>
                <div className="grid shrink-0 grid-cols-1 focus-within:relative">
                  <select
                    id="country"
                    name="country"
                    autoComplete="country"
                    aria-label="Country"
                    className={`col-start-1 row-start-1 w-full appearance-none rounded-md py-2 pl-3.5 pr-7 text-base placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-accent-primary sm:text-sm/6 ${
                      isDarkMode 
                        ? 'bg-gray-800 text-gray-300' 
                        : 'text-gray-500'
                    }`}
                  >
                    <option>IN</option>
                  </select>
                  <ChevronDownIcon
                    aria-hidden="true"
                    className={`pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end sm:size-4 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  />
                </div>
                <input
                  id="phone-number"
                  name="phone-number"
                  type="text"
                  placeholder="123-456-7890"
                  className={`block min-w-0 grow py-1.5 pl-1 pr-3 text-base placeholder:text-gray-400 focus:outline focus:outline-0 sm:text-sm/6 ${
                    isDarkMode 
                      ? 'bg-gray-800 text-white' 
                      : 'text-gray-900'
                  }`}
                />
              </div>
            </div>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="message" className={`block text-sm/6 font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Message
            </label>
            <div className="mt-2.5">
              <textarea
                id="message"
                name="message"
                rows={4}
                className={`block w-full rounded-md px-3.5 py-2 text-base outline outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-accent-primary ${
                  isDarkMode 
                    ? 'bg-gray-800 text-white outline-gray-700' 
                    : 'bg-white text-gray-900 outline-gray-300'
                }`}
                defaultValue={''}
              />
            </div>
          </div>
          <Field className="flex gap-x-4 sm:col-span-2">
            <div className="flex h-6 items-center">
              <Switch
                checked={agreed}
                onChange={setAgreed}
                className={`group flex w-8 flex-none cursor-pointer rounded-full p-px ring-1 ring-inset transition-colors duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary data-[checked]:bg-accent-primary ${
                  isDarkMode 
                    ? 'bg-gray-700 ring-gray-600' 
                    : 'bg-gray-200 ring-gray-900/5'
                }`}
              >
                <span className="sr-only">Agree to policies</span>
                <span
                  aria-hidden="true"
                  className={`size-4 transform rounded-full shadow-sm ring-1 transition duration-200 ease-in-out group-data-[checked]:translate-x-3.5 ${
                    isDarkMode 
                      ? 'bg-gray-800 ring-gray-600' 
                      : 'bg-white ring-gray-900/5'
                  }`}
                />
              </Switch>
            </div>
            <Label className={`text-sm/6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              By selecting this, you agree to our{' '}
              <a href="#" className="font-semibold text-accent-primary">
                privacy&nbsp;policy
              </a>
              .
            </Label>
          </Field>
        </div>
        <div className="mt-10">
          <button
            type="submit"
            className="block w-full rounded-md bg-accent-primary px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-accent-primary-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary"
          >
            Send Message
          </button>
        </div>
      </form>
    </div>
  )
}
